/**
 * 진행하기 단계의 prefill 계산.
 *
 * 저장된 상담 값을 그 화면의 질문 id 별 초기값으로 편다. 리뷰에서 "수정" 으로 들어왔을 때
 * 기존 답이 채워진 채로 열리는 것이 이 함수들의 몫이다.
 *
 * 순수 함수라 앱 밖에 둔다. 프리뷰가 다른 구현을 쓰면 "이미 입력된 채로 열리는가" 를
 * 프리뷰에서 확인하는 의미가 없어진다.
 */

/** 도착지 주소 화면의 질문 id. 문서(`progress-arrival-address`)와 짝을 이룬다. */
const Q_ADDRESS = 'address';
const Q_BUILDING = 'buildingName';
const Q_DONG = 'dong';
const Q_UNIT = 'unit';
const Q_FLOOR = 'floor';

/** "동 없음" 을 나타내는 센티넬. 문서의 선택지 값과 같아야 한다. */
export const NONE_DONG = '__none_dong__';

export interface FloorOrUnit {
  type: 'Floor' | 'Unit';
  value: string;
}

export interface StructuredAddress {
  roadAddress: string;
  buildingName?: string | null;
  dong?: string | null;
  floorOrUnit?: FloorOrUnit | null;
}

export function arrivalAddressToFlowValues(
  addr: StructuredAddress | null | undefined,
): Record<string, unknown> | undefined {
  if (!addr) return undefined;

  const out: Record<string, unknown> = {};
  out[Q_ADDRESS] = {
    roadAddress: addr.roadAddress,
    zipCode: '',
    ...(addr.buildingName ? { buildingName: addr.buildingName } : {}),
  };
  if (addr.buildingName) out[Q_BUILDING] = addr.buildingName;
  out[Q_DONG] = addr.dong ?? NONE_DONG;

  const fu = addr.floorOrUnit;
  if (fu?.type === 'Unit') {
    out[Q_UNIT] = fu.value;
  } else if (fu?.type === 'Floor') {
    // switchTo variant: 폼은 "호" 슬롯으로 열리고, "호 수 없음" 을 누르면 층 슬롯으로
    // 바뀌며 이 값이 보인다.
    out[Q_FLOOR] = fu.value;
  }
  return out;
}

/** 운반방식 화면의 질문 id. 문서(`progress-*-transport`)와 짝을 이룬다. */
const Q_METHOD = 'method';
const Q_ENTRANCE_LADDER = 'entranceSpaceLadder';
const Q_ENTRANCE_SKY = 'entranceSpaceSky';
const Q_RAMP = 'ramp';
const Q_STAIR_COUNT = 'stairCount';
const Q_HAS_ELEVATOR = 'hasElevator';

export interface MovingMethodValue {
  type: string;
  attributes?: Record<string, string>;
}

export function movingMethodToFlowValues(
  method: MovingMethodValue | null | undefined,
  hasElevator: boolean | null | undefined,
): Record<string, unknown> | undefined {
  if (!method) return undefined;

  const out: Record<string, unknown> = { [Q_METHOD]: method.type };
  const attrs = method.attributes ?? {};

  if (typeof attrs.entranceSpace === 'string' && attrs.entranceSpace.trim() !== '') {
    if (method.type === 'LADDER_TRUCK') out[Q_ENTRANCE_LADDER] = attrs.entranceSpace;
    else if (method.type === 'SKY_LIFT') out[Q_ENTRANCE_SKY] = attrs.entranceSpace;
  }
  if (typeof attrs.ramp === 'string') {
    out[Q_RAMP] = attrs.ramp === 'true';
  }
  if (typeof attrs.stairCount === 'string' && attrs.stairCount.trim() !== '') {
    out[Q_STAIR_COUNT] = attrs.stairCount;
  }
  if (typeof hasElevator === 'boolean') out[Q_HAS_ELEVATOR] = hasElevator;

  return out;
}
